import { createContext } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import type { GlobalStateType } from 'Lib/reduxType';
import { PropsWithRequiredChildren } from 'Types';

import { getIsProfileLoaded } from 'Store/app/selectors';
import {
  getHasGoldSubscription,
  getHasPlatinumSubscription,
} from 'Store/profile/revenue.selectors';

/**
 * Each top-level UI theme should have a constant defined here.
 */
export const UI_THEME_UNKNOWN = 'unknown';
export const UI_THEME_REGULAR = 'regular';
export const UI_THEME_GOLD = 'gold';
export const UI_THEME_PLATINUM = 'platinum';
export const UI_THEME_BOOST = 'boost';
export const UI_THEME_PRIMETIME_BOOST = 'primetime-boost';
export const UI_THEME_SELECT = 'select';

export type UserTheme =
  | typeof UI_THEME_UNKNOWN
  | typeof UI_THEME_REGULAR
  | typeof UI_THEME_GOLD
  | typeof UI_THEME_PLATINUM
  | typeof UI_THEME_SELECT;
export type Theme = UserTheme | typeof UI_THEME_BOOST | typeof UI_THEME_PRIMETIME_BOOST;

/**
 * Determines the default UI theme from the provided state
 *
 * @param {GlobalStateType} state - top-level state
 */
export function getUiThemeForUser(state: GlobalStateType): UserTheme {
  switch (true) {
    case !getIsProfileLoaded(state):
      return UI_THEME_UNKNOWN;
    case getHasPlatinumSubscription(state):
      return UI_THEME_PLATINUM;
    case getHasGoldSubscription(state):
      return UI_THEME_GOLD;
    default:
      return UI_THEME_REGULAR;
  }
}

/**
 * UI Theme Context Provider/Consumer
 */
const UiThemeContext = createContext(UI_THEME_REGULAR);
export const UiThemeConsumer = UiThemeContext.Consumer;

type UiThemeProviderOwnProps = PropsWithRequiredChildren<{
  theme?: Theme;
}>;
type UiThemeProviderProps = UiThemeProviderOwnProps & ConnectedProps<typeof storeConnector>;

function mapStateToProps(state: GlobalStateType, ownProps: UiThemeProviderOwnProps) {
  return {
    theme: typeof ownProps.theme !== 'undefined' ? ownProps.theme : getUiThemeForUser(state),
  };
}

const storeConnector = connect(mapStateToProps);
export const UiThemeProvider = storeConnector(({ children, theme }: UiThemeProviderProps) => (
  <UiThemeContext.Provider value={theme}>{children}</UiThemeContext.Provider>
));

export default UiThemeContext;
